import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

/**
 * Shared responsive primary nav. Renders the SAME pre-filtered `items` two ways
 * from one source:
 *   - wide  (hidden md:flex): a horizontal row of links.
 *   - narrow (md:hidden):     a hamburger button + dropdown.
 *
 * The CALLER computes the role/entitlement-filtered `items` and passes them in —
 * this component never decides visibility, it only renders. That lets the dealer,
 * SuperAdmin/platform, and SubAdmin navs differ while sharing one responsive
 * implementation (and any future nav gets the collapse behavior for free).
 *
 * Behavior: the dropdown closes on select, on outside-click, and on Escape (which
 * returns focus to the toggle). Links are focusable; the toggle exposes
 * aria-haspopup/aria-expanded; the active item gets aria-current="page".
 *
 * @param {{items: Array<{path:string,label:string,icon?:Function}>, ariaLabel?:string}} props
 */
export function ResponsiveNav({ items = [], ariaLabel = 'Primary' }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);

  // Close the dropdown on outside-click + Escape (Escape restores focus to the
  // toggle for keyboard users).
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { setOpen(false); btnRef.current?.focus(); }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Honor an optional `match` prefix so a hub item (e.g. CRM at /crm/my-day with
  // match:'/crm') stays highlighted across all of its sub-routes/tabs, not just
  // its exact landing path.
  const isActive = (item) =>
    location.pathname === item.path
    || (item.match && (location.pathname === item.match
        || location.pathname.startsWith(item.match + '/')));
  const testId = (label) => `nav-${label.toLowerCase().replace(/\s+/g, '-')}`;

  if (!items.length) return null;

  return (
    <>
      {/* Wide screens — horizontal row */}
      <nav aria-label={ariaLabel} className="hidden md:flex ml-8 gap-1">
        {items.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={testId(item.label)}
              aria-current={active ? 'page' : undefined}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                active ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground'
              }`}
            >
              {Icon ? <Icon className="h-4 w-4" /> : null}
              {item.label}
            </Link>
          );
        })}
      </nav>

      {/* Narrow screens — hamburger + dropdown */}
      <div ref={wrapRef} className="md:hidden relative ml-2">
        <button
          ref={btnRef}
          type="button"
          data-testid="mobile-menu-button"
          aria-label="Menu"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="p-2 hover:bg-accent rounded-lg transition-colors"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
        {open && (
          <nav
            aria-label={ariaLabel}
            className="absolute left-0 mt-2 w-56 max-h-[70vh] overflow-auto rounded-lg border border-border bg-card shadow-lg p-2 z-50"
          >
            {items.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`m-${testId(item.label)}`}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => setOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors mb-0.5 ${
                    active ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-accent'
                  }`}
                >
                  {Icon ? <Icon className="h-5 w-5" /> : null}
                  {item.label}
                </Link>
              );
            })}
          </nav>
        )}
      </div>
    </>
  );
}

export default ResponsiveNav;
